import { Fragment } from 'react'

import { FormattedMessage, InjectedIntlProps, injectIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'

import Confirmation from '@components/UI/Confirmation'
import { FooterButtonWrapper } from '@components/UI/Confirmation/Confirmation.styled'
import { IApplicationState } from '@store'
import {
  selectCampaignTitle,
  selectCopyCampaignError,
  selectIsCopyingCampaign,
} from '@store/campaigns'
import { copyCampaign } from '@store/campaigns/actions/copyCampaign'
import { Button } from '@tribegroup'

interface ICampaignCardModalCopyCampaignProps extends InjectedIntlProps {
  campaignId: number
  onCancel: VoidFunction
  isOpen: boolean
}

export const CampaignCardModalCopyCampaign = ({
  onCancel,
  isOpen,
  campaignId,
  intl,
}: ICampaignCardModalCopyCampaignProps) => {
  const dispatch = useDispatch()
  const { campaignTitle, isCopying } = useSelector((state: IApplicationState) => ({
    campaignTitle: selectCampaignTitle(state, campaignId),
    isCopying: selectIsCopyingCampaign(state, campaignId),
    copyError: selectCopyCampaignError(state, campaignId),
  }))

  const onConfirm = () => {
    if (isCopying) {
      return
    }
    dispatch(
      copyCampaign(
        campaignId,
        onCancel,
        intl.formatMessage({
          id: 'campaign.campaignCard.menuAction.copyCampaign.confirmation.copySuccess',
        }),
      ),
    )
  }

  return (
    <Confirmation
      title={
        <FormattedMessage id="campaign.campaignCard.menuAction.copyCampaign.confirmation.title" />
      }
      content={
        <FormattedMessage
          id="campaign.campaignCard.menuAction.copyCampaign.confirmation.body"
          values={{ campaignTitle }}
        />
      }
      footerButtons={
        <Fragment>
          {!isCopying && (
            <FooterButtonWrapper>
              <Button
                scheme="primary"
                quiet
                onClick={onCancel}
                data-cy-element="copy-campaign-modal-cancel-button"
              >
                <FormattedMessage id="core.text.cancel" />
              </Button>
            </FooterButtonWrapper>
          )}
          <FooterButtonWrapper>
            <Button
              scheme="primary"
              onClick={onConfirm}
              data-cy-element="copy-campaign-modal-activate-button"
              disabled={isCopying}
              loading={isCopying}
            >
              <FormattedMessage
                id={
                  isCopying
                    ? 'campaign.campaignCard.menuAction.copyCampaign.confirmation.copying'
                    : 'campaign.campaignCard.menuAction.text.copyCampaign'
                }
              />
            </Button>
          </FooterButtonWrapper>
        </Fragment>
      }
      isOpen={isOpen}
      onCloseIconClick={onCancel}
    />
  )
}

export default injectIntl(CampaignCardModalCopyCampaign)
